import * as React from 'react';
import { NewMultiSelect } from '../../../../components/synced/ui/NewMultiSelect/NewMultiSelect';

import styles from './SignatoryEditor.module.css';
import { SignerFormApi } from './useSignerForm';
import { ProjectDocSigner } from '../../models/ProjectDocSigner';
import { ProjectDocSignerRoleCode } from '../../models/enums/ProjectDocSignerRoleCode';
import { FormField } from '../../../../components/synced/ui/FormField/FormField';
import { FormSectionCard } from '../../../../components/synced/ui/FormSectionCard/FormSectionCard';
import { Toggle } from '../../../../components/synced/ui/Toggle/Toggle';
import { UploadDropzone } from '../../../../components/synced/ui/UploadDropzone/UploadDropzone';
import { NewButton } from '../../../../components/synced/ui/NewButton/NewButton';
import { Pill } from '../../../../components/synced/ui/Pill/Pill';
import { ProgressBar } from '../../../../components/synced/ui/ProgressBar/ProgressBar';
import { EmptyState } from '../../../../components/synced/ui/EmptyState/EmptyState';

interface SignatoryEditorProps {
  api: SignerFormApi;
  index: number;
}

export const SignatoryEditor: React.FC<SignatoryEditorProps> = ({ api, index }) => {
  const signer: Partial<ProjectDocSigner> | undefined = api.signers[index];

  if (!signer) {
    return <EmptyState icon="fa-user-pen" title="Выберите участника" text="Выберите участника из списка слева, чтобы отредактировать его данные." />;
  }

  const readonly = api.readonly;
  const isDesigner = signer.RoleId === ProjectDocSignerRoleCode.Designer;
  const isOther = signer.RoleId === ProjectDocSignerRoleCode.Other;
  const isActParty = signer.RoleId === ProjectDocSignerRoleCode.ActParty;
  const isNrssIdOptional = signer.RoleId === ProjectDocSignerRoleCode.Contractor
    || signer.RoleId === ProjectDocSignerRoleCode.SkContractor;
  const needsRep = !isDesigner || !!signer.HasAuthorSupervision;
  const repDisabled = readonly || (isDesigner && !signer.HasAuthorSupervision);
  const hasSavedDoc = !!signer.BasisDocumentId && !signer.BasisDocumentUpload;
  const hasPendingDoc = !!signer.BasisDocumentUpload?.Content;

  const completion = api.completionOf(signer);
  const percent = completion.total > 0 ? Math.round((completion.filled / completion.total) * 100) : 0;

  const orgField = (label: string, field: keyof ProjectDocSigner, span: string, maxLength: number) => (
    <FormField
      label={label}
      span={span}
      value={(signer[field] as string) ?? ''}
      disabled={readonly || api.contractorLocked(index, field as string)}
      invalid={api.errClass(index, field as string)}
      maxLength={maxLength}
      onChange={(v) => api.updateSigner(index, field, v)} />
  );

  const repField = (label: string, field: keyof ProjectDocSigner, span: string, maxLength: number) => (
    <FormField
      label={label}
      span={span}
      value={(signer[field] as string) ?? ''}
      disabled={repDisabled}
      invalid={api.errClass(index, field as string)}
      maxLength={maxLength}
      onChange={(v) => api.updateSigner(index, field, v)} />
  );

  const actTypeOptions = api.getAvailableActTypes(index).concat(
    api.actTypes.filter(at => at.Id !== undefined && (signer.ActTypeBindings ?? []).includes(at.Id!))
  ).filter((at, i, arr) => arr.findIndex(a => a.Id === at.Id) === i);

  return (
    <div className={styles.root}>
      {/* header */}
      <div className={styles.header}>
        <div className={styles.headerMain}>
          <div className={styles.titleRow}>
            <span className={styles.role}>{api.getLabel(signer)}</span>
            <Pill label={api.getSubLabel(signer)} bg="var(--status-draft-bg)" color="var(--text-tertiary)" />
          </div>
          <div className={styles.progressRow}>
            <ProgressBar percent={percent} width={110} size="md" />
            <span className={styles.progressLine}>Заполнено {completion.filled} из {completion.total}</span>
          </div>
        </div>
        {api.canRemove(signer) && (
          <NewButton variant="danger" icon="fa-regular fa-trash-can" onClick={() => api.removeSigner(index)}>
            Удалить
          </NewButton>
        )}
      </div>

      {/* sections */}
      <div className={styles.sections}>
        {isOther && !readonly && (
          <FormSectionCard icon="fa-regular fa-building" title="Наименование лица">
            <FormField
              label="Название участвующего лица"
              span="span 12"
              value={signer.CustomRoleName ?? ''}
              placeholder="Название лица"
              maxLength={500}
              onChange={(v) => api.updateSigner(index, 'CustomRoleName', v)} />
          </FormSectionCard>
        )}

        <FormSectionCard icon="fa-regular fa-building" title="Юридическое лицо">
          {orgField('Наименование ЮЛ', 'OrgName', 'span 6', 500)}
          {orgField('ОГРН', 'Ogrn', 'span 3', 20)}
          {orgField('ИНН ЮЛ', 'InnOrg', 'span 3', 12)}
          {orgField('Адрес', 'Address', 'span 6', 1000)}
          {orgField('Телефон/факс', 'PhoneFax', 'span 6', 100)}
          {api.isElectronic && orgField('Идентификатор EDI-провайдера', 'EdiProviderId', 'span 6', 255)}
          {isActParty && (
            <FormField label="Типы актов" span="span 12">
              <NewMultiSelect
                disabled={readonly}
                filter
                filterPlaceholder="Поиск..."
                value={signer.ActTypeBindings ?? []}
                options={actTypeOptions}
                optionLabel="Title"
                optionValue="Id"
                placeholder="Выберите типы актов..."
                className="w-100"
                onChange={e => api.updateSigner(index, 'ActTypeBindings', e.value)} />
            </FormField>
          )}
        </FormSectionCard>

        {isDesigner && (
          <div className={styles.authorSupervisionBar}>
            <Toggle
              label="Авторский надзор"
              checked={signer.HasAuthorSupervision ?? false}
              disabled={readonly}
              onChange={(checked) => {
                if (!checked) {
                  api.setSignerFields(index, {
                    HasAuthorSupervision: false,
                    RepJobTitle: '', RepLastName: '', RepFirstName: '', RepMiddleName: '',
                    RepInn: '', AuthorityDocType: '', AuthorityDocDetails: '', NrssId: '',
                  });
                } else {
                  api.updateSigner(index, 'HasAuthorSupervision', true);
                }
              }} />
          </div>
        )}

        {needsRep && (
          <FormSectionCard icon="fa-regular fa-user" title="Представитель">
            {repField('Должность', 'RepJobTitle', 'span 6', 500)}
            {repField('Фамилия', 'RepLastName', 'span 3', 255)}
            {repField('Имя', 'RepFirstName', 'span 3', 255)}
            {repField('Отчество', 'RepMiddleName', 'span 4', 255)}
            {api.isElectronic && repField('ИНН ФЛ', 'RepInn', 'span 4', 12)}
            {repField('Документ, подтверждающий полномочия', 'AuthorityDocType', 'span 4', 500)}
            {repField('Реквизиты документа', 'AuthorityDocDetails', 'span 6', 1000)}
            {repField(
              isNrssIdOptional
                ? 'Идентификатор в реестре специалистов (НОСТРОЙ, необязательно)'
                : 'Идентификатор в реестре специалистов (НОСТРОЙ)',
              'NrssId',
              'span 6',
              255)}
          </FormSectionCard>
        )}

        {needsRep && (
          <FormSectionCard icon="fa-solid fa-file-signature" title="Скан документа, подтверждающего полномочия">
            <div className={styles.uploadCell}>
              <UploadDropzone
                hasFile={hasSavedDoc || hasPendingDoc}
                fileName={hasPendingDoc ? signer.BasisDocumentUpload!.FileName : ((signer.BasisDocument as any)?.Title ?? 'Документ')}
                fileMeta={hasPendingDoc ? `Готово к сохранению` : undefined}
                accept="application/pdf"
                hint="PDF · до 25 МБ"
                disabled={readonly}
                invalid={api.errClass(index, 'BasisDocument')}
                onPick={(file) => api.handleFileUpload(index, file)}
                onRemove={() => api.removeBasisDocument(index)}
                onDownload={hasSavedDoc && signer.Id ? () => api.downloadBasisDocument(signer.Id!) : undefined}
                isDownloading={api.isDownloadingDocument} />
            </div>
          </FormSectionCard>
        )}
      </div>

      {/* footer */}
      {!readonly && (
        <div className={styles.footer}>
          <NewButton variant="primary" icon="fa-solid fa-check" onClick={() => api.save()} disabled={api.isLoading}>
            Сохранить
          </NewButton>
          <span className={styles.saveHint}>Данные подписантов используются при формировании актов</span>
        </div>
      )}
    </div>
  );
};
